import { useSignInHandlers } from "scripts/hooks/useSignInHandlers";
import { useQueryFlags } from "scripts/hooks/useQueryFlags";
import { IUser } from "services/UserContext";

interface GuestLandingProps {
    user?: IUser;
}

interface IFeature {
    title: string;
    description: string;
    variant: "chat" | "home" | "bolt";
    icon: JSX.Element;
}

const features: IFeature[] = [
    {
        title: "Natural Conversations",
        description: "Ask in plain language — just like you would a person.",
        variant: "chat",
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-4 4v-4H5.5A1.5 1.5 0 0 1 4 14.5v-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: "Smarter Results",
        description: "Get personalized and relevant property insights.",
        variant: "home",
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="m4 11 8-6 8 6M6 9.5V19h12V9.5M10 19v-5h4v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: "Save Time",
        description: "Find what you need, faster.",
        variant: "bolt",
        icon: (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M13 3 5 13h5l-1 8 8-10h-5l1-8Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
];

/**
 * Guest landing shown when access is blocked (GUEST_USER_ALLOWED off). Sign-in
 * handlers live here so `useSignInHandlers` runs inside the ShareContext tree.
 */
export const GuestLanding = ({ user }: GuestLandingProps) => {
    const { handleSignIn, handleCreateAccount } = useSignInHandlers(user?.user_id);
    const { embed } = useQueryFlags();

    return (
        <div className="guest_landing">
            <p className="guest_subtitle">
                Ask questions, discover property insights, and search naturally — just like you would a person.
            </p>

            <div className="guest_actions">
                {/* The #registerLogin modal only mounts outside embed mode, so
                    embedded guests fall back to a direct sign-in redirect. */}
                <button
                    type="button"
                    className="btn btn--primary btn--large guest_btn"
                    {...(embed
                        ? { onClick: handleSignIn }
                        : { "data-toggle": "modal", "data-target": "#registerLogin" })}
                >
                    Sign In <span aria-hidden="true">&rarr;</span>
                </button>
                <button
                    type="button"
                    className="btn btn--large guest_btn guest_btn--outline"
                    onClick={handleCreateAccount}
                >
                    Create an Account
                </button>
            </div>

            <div className="guest_features">
                {features.map((feature) => (
                    <div key={feature.title} className="feature_card">
                        <span className={`feature_icon feature_icon--${feature.variant}`}>{feature.icon}</span>
                        <div className="feature_title">{feature.title}</div>
                        <div className="feature_desc">{feature.description}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};
